import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { DownloadIcon, PackageIcon, ShieldCheckIcon } from "lucide-react";

import { useAnalytics } from "@/common/analytics";
import { detectClientPlatform } from "@/common/detectPlatform";
import { Button } from "@/components/ui/Button";
import { CoworkScopeNote } from "@/features/wonda/components/CoworkScopeNote";
import { InstallCommand } from "@/features/wonda/components/InstallCommand";
import {
  MAC_INSTALLER_ASSET,
  WINDOWS_INSTALLER_ASSET,
} from "@/features/wonda/constants";
import {
  deviceNoun,
  useDetectedOs,
  WINDOWS_SMARTSCREEN_NOTE,
} from "@/features/wonda/osInstall";
import { useReleaseDownload } from "@/features/wonda/releaseDownloads";
import { CliNav } from "@/landing/components/CliNav";
import { MinimalFooter } from "@/landing/components/MinimalFooter";
import WabDockIcon from "@/landing/wab/assets/wab-dock-icon.png?url";

export const Route = createFileRoute("/_authenticated/(wonda)/_wonda/download")(
  {
    component: DownloadPage,
  },
);

function DownloadPage() {
  const os = useDetectedOs();
  const isWindows = os === "windows";
  const noun = deviceNoun(os);
  const trackEvent = useAnalytics();
  // `os` above drives which installer this page offers and is binary by
  // design, so it reports "macos" for Linux and mobile alike. The event needs
  // what the visitor actually is, hence the separate detector.
  // Releases are private: the button asks the API for a signed link as the
  // signed-in account, then the browser downloads it.
  const download = useReleaseDownload();
  const startInstaller = (event: React.MouseEvent, asset: string) => {
    event.preventDefault();
    trackEvent("install_cta_clicked", {
      surface: "download_page",
      method: "app_installer",
      os: detectClientPlatform(),
      placement: "hero",
    });
    void download.start(asset);
  };
  return (
    <div className="flex min-h-[100dvh] flex-col">
      <CliNav />
      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-6 py-10">
        <div className="mt-10 max-w-2xl">
          <img
            src={WabDockIcon}
            alt=""
            className="size-20 rounded-2xl md:size-24"
          />
          <h1 className="mt-6 text-4xl font-semibold tracking-normal">
            {isWindows ? "Install the Wonda app" : "Install the Wonda Mac app"}
          </h1>
          <p className="mt-4 text-base leading-7 text-[var(--steward-muted)]">
            {isWindows
              ? "Download the installer and run it once. Wonda runs in the background and starts automatically every time you log in."
              : "Download the app and open it once. It runs in the background and starts automatically every time you log in."}
          </p>
          <CoworkScopeNote className="mt-4" />
        </div>

        {isWindows ? (
          <>
            <div className="mt-10 grid gap-4 rounded-lg border border-[var(--hair)] bg-[var(--ground)] p-5 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  <PackageIcon className="size-4" />
                  Everything in one installer
                </div>
                <p className="mt-2 text-sm text-[var(--steward-muted)]">
                  Brings its own Node, so there is nothing to set up first. No
                  administrator rights needed, and the relay comes back on its
                  own after a restart.
                </p>
              </div>
              <Button
                variant="steward"
                size="md"
                disabled={download.pending}
                onClick={(event) =>
                  startInstaller(event, WINDOWS_INSTALLER_ASSET)
                }
              >
                <DownloadIcon className="size-4" />
                {download.pending ? "Starting…" : "Download for Windows"}
              </Button>
            </div>
            <p className="mt-3 rounded-lg border border-[var(--hair)] bg-[var(--ground)] px-5 py-3 text-sm text-[var(--steward-muted)]">
              {WINDOWS_SMARTSCREEN_NOTE}
            </p>
          </>
        ) : (
          <div className="mt-10 grid gap-4 rounded-lg border border-[var(--hair)] bg-[var(--ground)] p-5 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium">
                <ShieldCheckIcon className="size-4" />
                Developer ID signed and notarized
              </div>
              <p className="mt-2 text-sm text-[var(--steward-muted)]">
                The Wonda Mac app keeps running in the background and comes back
                on its own after a restart.
              </p>
            </div>
            <Button
              variant="steward"
              size="md"
              disabled={download.pending}
              onClick={(event) => startInstaller(event, MAC_INSTALLER_ASSET)}
            >
              <DownloadIcon className="size-4" />
              {download.pending ? "Starting…" : "Download for Mac"}
            </Button>
          </div>
        )}
        {download.error && (
          <p className="mt-3 text-sm text-red-600">{download.error}</p>
        )}

        <details className="mt-6 rounded-lg border border-[var(--hair)] bg-[var(--ground)] px-5 py-4">
          <summary className="cursor-pointer text-sm font-medium">
            Prefer the terminal? Install with one command
          </summary>
          <p className="mt-3 text-sm text-[var(--steward-muted)]">
            For macOS and Linux. It installs the Wonda CLI and signs it in to
            this account.
          </p>
          <InstallCommand
            className="mt-4"
            onCopied={() =>
              trackEvent("install_cta_clicked", {
                surface: "download_page",
                method: "curl_copy",
                os: detectClientPlatform(),
                placement: "terminal_install",
              })
            }
          />
        </details>

        <details className="mt-6 rounded-lg border border-[var(--hair)] bg-[var(--ground)] px-5 py-4">
          <summary className="cursor-pointer text-sm font-medium">
            Advanced: what gets installed
          </summary>
          <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-[var(--steward-muted)]">
            <li>
              A background relay that Claude Cowork uses to reach this {noun}.
            </li>
            <li>
              The Wonda Automation Browser, a dedicated browser kept separate
              from your everyday one, where your social accounts stay logged in.
            </li>
            <li>
              {isWindows
                ? "A per-user Scheduled Task that starts the relay again after login or restart."
                : "A per-user LaunchAgent that starts the relay again after login or restart."}
            </li>
          </ul>
        </details>

        <Link
          to="/setup"
          className="mt-6 text-sm font-medium underline underline-offset-4"
        >
          Already installed? Continue setup
        </Link>
      </main>
      <MinimalFooter />
    </div>
  );
}
